export type RowStatus = 'pending' | 'processing' | 'success' | 'error';

export interface ImportRow {
  index: number;
  name: string;
  email: string;
  phone: string;
  status: RowStatus;
  error?: string;
  driverId?: number;
}

import { validateDisplayName, validateEmail, validatePhone } from '@services/validationService';

// ── Validation ──────────────────────────────────────────────────────────

export const isValidDisplayName = (name: string) => validateDisplayName(name);
export const isValidEmail = (email: string) => validateEmail(email);
export const isValidPhone = (phone: string) => validatePhone(phone);

export const validateRow = (row: ImportRow): ImportRow => {
  const errors: string[] = [];
  if (!isValidDisplayName(row.name)) errors.push('Invalid name');
  if (!isValidEmail(row.email)) errors.push('Invalid email');
  if (!isValidPhone(row.phone)) errors.push('Invalid phone');
  return errors.length > 0 ? { ...row, status: 'error' as RowStatus, error: errors.join(', ') } : row;
};

// ── CSV Parsing ─────────────────────────────────────────────────────────

export const parseCsvData = (results: any): ImportRow[] => {
  const data: string[][] = results?.data || [];
  if (data.length === 0) return [];

  // Detect header row
  let startIndex = 0;
  const firstRow = data[0];
  if (
    firstRow &&
    firstRow.length >= 3 &&
    firstRow[0]?.toLowerCase().trim() === 'name' &&
    firstRow[1]?.toLowerCase().trim() === 'email' &&
    firstRow[2]?.toLowerCase().trim() === 'phone'
  ) {
    startIndex = 1;
  }

  const parsed: ImportRow[] = [];
  for (let i = startIndex; i < data.length; i++) {
    const row = data[i];
    if (!row || row.every((cell: string) => !cell?.trim())) continue;

    parsed.push({
      index: parsed.length,
      name: (row[0] || '').trim(),
      email: (row[1] || '').trim(),
      phone: (row[2] || '').trim(),
      status: 'pending',
    });
  }

  return parsed.map(validateRow);
};

// ── Error Formatting ────────────────────────────────────────────────────

export const formatDriverImportError = (error: any): string => {
  const raw: string =
    error?.graphQLErrors?.[0]?.message || error?.extensions?.code || error?.message || '';

  if (
    raw.includes('constraint-violation') ||
    raw.includes('duplicate key') ||
    raw.includes('Uniqueness violation')
  ) {
    return 'A driver with this email already exists';
  }
  if (raw.includes('network') || raw.includes('Network')) {
    return 'Network error — please check your connection';
  }
  if (raw.includes('not-found') || raw.includes('No driver returned')) {
    return 'Driver could not be created — please try again';
  }
  if (raw) return raw;
  return 'An unexpected error occurred';
};

// ── Duplicate Detection ─────────────────────────────────────────────────

export const findDuplicateIndices = (
  pendingRows: ImportRow[],
  existingEmails: Set<string>
): Set<number> => {
  const duplicates = new Set<number>();
  for (const row of pendingRows) {
    if (existingEmails.has(row.email.toLowerCase())) {
      duplicates.add(row.index);
    }
  }
  return duplicates;
};
